/* ============================================================
   ATHLO+ — "Do this next" rail (site-wide funnel bridges)
   Extends the locker onboarding card's next-step pattern to the
   three funnel hand-offs the owner asked for:
     · quiz result (the-playground, archetype saved) → meet your tribe
     · a coaching plan (/pages/coaching)             → meet your coach
     · gear (any product page)                       → the plan that uses it
   Renders ONE slim rail above the footer — only on those pages, so
   it never competes with a page's primary CTA elsewhere.
   Removable: delete this file + its loader line in theme.liquid.
   ============================================================ */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function archetype() {
    try { return JSON.parse(localStorage.getItem('hub:archetype') || 'null'); } catch (e) { return null; }
  }

  // A signed-in member who has not built anything yet. This is the 238: they took the quiz,
  // signed in to keep the archetype, and never opened the Locker — so they hold points and a
  // result and an empty profile, and the directory lists them as "ATHLETE".
  //
  // DELIBERATELY NOT `name`. Since 2026-08-19 HubProfile.get() falls back to the Shopify first
  // name when the stored one is blank, so a name is no longer evidence the member did anything.
  // These four are things only they can put there — avatarKit included, because "Dress my
  // athlete" on the result screen patches it and that IS engaging with the locker.
  function profileEmpty() {
    var m = window.HubMember, HP = window.HubProfile;
    if (!m || !m.is || !m.is() || !HP || !HP.get) return false;
    var p;
    try { p = HP.get() || {}; } catch (e) { return false; }
    return !(p.avatarKit || (p.bio || '').trim() || (p.homeBase || '').trim() ||
             ((p.interests || []).length));
  }

  // Which bridge (if any) belongs on this page.
  function context() {
    var path = location.pathname.replace(/\/+$/, '');
    var isProduct = /(^|\s)template-product(\s|$)/.test(document.body.className);

    if (isProduct) {
      var ap = archetype();
      var sport = (ap && ap.sportName) ? String(ap.sportName) : '';
      return {
        key: 'gear-to-plan',
        eyebrow: 'Do this next',
        title: sport ? 'Put it to work in your ' + sport + ' plan' : 'The plan that puts it to work',
        sub: sport
          ? 'Gear’s only half the story — see the coach-built ' + sport + ' programmes made for where you’re headed.'
          : 'Gear’s only half the story — coach-built programmes for every level.',
        cta: 'See the plans',
        href: '/pages/coaching'
      };
    }
    // THE QUIZ LIVES ON /pages/trivia-games. This gate named only /pages/the-playground, which
    // holds a hero, a nav helper and a multicolumn — no quiz. So the one on-site next step for
    // somebody who had just found their archetype had not fired since the quiz moved. Both are
    // matched now: the-playground and playground still carry an archetype from an earlier visit,
    // and the bridge is worth showing there too.
    if (/\/pages\/(trivia-games|the-playground|playground)$/.test(path)) {
      var a = archetype();
      if (!a || !a.title) return null;         // no result yet — the quiz itself is the next step
      // A member with nothing in their locker is asked for the locker, not for a tribe. Getting
      // the profile built is what turns a quiz result into an athlete other people can find; the
      // tribe is the better ask once there is something to show.
      // 2026-10-06 (owner): "customise", not "claim". Every signed-in member ALREADY has a Locker -
      // it is their account, and it already shows their archetype (members.archetype), points and
      // tier. "Claim" told them they had to make something; what is left is to make it theirs.
      if (profileEmpty()) {
        return {
          key: 'quiz-to-locker',
          eyebrow: 'Do this next',
          title: 'Customise your Locker',
          sub: 'Your Locker is ready — your archetype and points are already in it. Add a photo and a few details to make it yours, and it goes on your athlete profile where the community can find you.',
          cta: 'Customise my Locker',
          href: '/pages/my-locker'
        };
      }
      return {
        key: 'quiz-to-tribe',
        eyebrow: 'Do this next',
        title: 'Meet your tribe',
        sub: (a.sportName ? 'Your ' + a.sportName + ' people are already training together.' : 'Your people are already training together.') + ' Find your crew.',
        cta: 'Choose your tribe',
        href: '/pages/choose-your-tribe'
      };
    }
    if (/\/pages\/coaching$/.test(path)) {
      return {
        key: 'plan-to-coach',
        eyebrow: 'Do this next',
        title: 'Meet your coach',
        sub: 'Every plan here is built by a real coach — see who’s behind yours.',
        cta: 'Meet the coaches',
        href: '#coaches',
        onClick: function (e) {
          var t = document.querySelector('.coaching__subh');
          if (t) { e.preventDefault(); t.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
        }
      };
    }
    return null;
  }

  function css() {
    if (document.getElementById('hub-donext-css')) return;
    var st = document.createElement('style');
    st.id = 'hub-donext-css';
    st.textContent =
      '.hub-donext{margin:3rem auto 0;padding:0 var(--pad,1.5rem) 3.5rem;max-width:var(--maxw,1280px)}' +
      '.hub-donext__card{display:flex;align-items:center;gap:1.1rem;max-width:640px;padding:1rem 1.2rem;' +
      'background:var(--ink-800,#0b0e12);border:1px solid var(--line,rgba(255,255,255,.12));border-radius:14px;' +
      'text-decoration:none;color:var(--fg,#fff);transition:border-color .18s,background .18s,transform .18s}' +
      '@media (hover:hover) and (pointer:fine){.hub-donext__card:hover{border-color:var(--lime,#c8ff00);background:rgba(200,255,0,.04);transform:translateY(-2px)}}' +
      '.hub-donext__body{flex:1;min-width:0;display:flex;flex-direction:column;gap:.15rem}' +
      '.hub-donext__eyebrow{font-family:var(--font-mono,monospace);font-size:.56rem;letter-spacing:.14em;text-transform:uppercase;color:var(--lime,#c8ff00)}' +
      '.hub-donext__title{font-family:var(--font-display,inherit);font-size:1.15rem;line-height:1.1;text-transform:uppercase;letter-spacing:.01em}' +
      '.hub-donext__sub{font-size:.8rem;color:var(--muted,#8a93a0);line-height:1.4}' +
      '.hub-donext__cta{flex:0 0 auto;display:inline-flex;align-items:center;gap:.45rem;font-family:var(--font-mono,monospace);' +
      'font-size:.66rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--lime,#c8ff00);white-space:nowrap}' +
      '.hub-donext__arrow{transition:transform .18s}' +
      '@media (hover:hover) and (pointer:fine){.hub-donext__card:hover .hub-donext__arrow{transform:translateX(3px)}}' +
      '@media (max-width:560px){.hub-donext__card{flex-direction:column;align-items:flex-start;gap:.7rem}}' +
      '@media (prefers-reduced-motion:reduce){.hub-donext__card,.hub-donext__arrow{transition:none}}';
    (document.head || document.documentElement).appendChild(st);
  }

  var mountedKey = null;

  function mount() {
    var ctx = context();
    var existing = document.querySelector('.hub-donext');
    if (!ctx) {
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      mountedKey = null;
      return;
    }
    if (mountedKey === ctx.key && existing) return;   // same bridge as last time — leave it alone
    var footer = document.querySelector('.shopify-section-footer, #shopify-section-footer, footer');
    if (!footer) return;
    css();

    var wrap = document.createElement('aside');
    wrap.className = 'hub-donext';
    wrap.setAttribute('data-donext', ctx.key);
    wrap.setAttribute('aria-label', 'Suggested next step');

    var card = document.createElement('a');
    card.className = 'hub-donext__card';
    card.href = ctx.href;
    card.addEventListener('click', function () { try { if (window.HubTrack) window.HubTrack('donext_click', { bridge: ctx.key }); } catch (e) {} });
    if (ctx.onClick) card.addEventListener('click', ctx.onClick);

    var body = document.createElement('div');
    body.className = 'hub-donext__body';
    var eb = document.createElement('span'); eb.className = 'hub-donext__eyebrow'; eb.textContent = ctx.eyebrow;
    var tt = document.createElement('span'); tt.className = 'hub-donext__title'; tt.textContent = ctx.title;
    var sb = document.createElement('span'); sb.className = 'hub-donext__sub'; sb.textContent = ctx.sub;
    body.appendChild(eb); body.appendChild(tt); body.appendChild(sb);

    var cta = document.createElement('span');
    cta.className = 'hub-donext__cta';
    cta.innerHTML = ctx.cta + ' <span class="hub-donext__arrow" aria-hidden="true">→</span>';

    card.appendChild(body);
    card.appendChild(cta);
    wrap.appendChild(card);

    if (existing && existing.parentNode) {
      existing.parentNode.replaceChild(wrap, existing);
    } else {
      var host = footer.closest('.shopify-section') || footer;
      host.parentNode.insertBefore(wrap, host);
    }
    mountedKey = ctx.key;
  }

  ready(function () {
    mount();
    // THE QUIZ WRITES ITS RESULT AFTER LOAD, on the very page this rail now lives on. Deciding
    // once at DOMContentLoaded reads "no archetype yet", returns null and never looks again — so
    // the person who has just finished the quiz, the one this bridge exists for, is the one who
    // never saw it. sendResponse() calls HubProfile.persist(), which emits `hub:profile`;
    // restoring an archetype after a sign-in emits `hub:archetype`. Either means re-decide.
    // Cheap to re-run: mount() returns immediately when the chosen bridge has not changed.
    window.addEventListener('hub:profile', mount);
    window.addEventListener('hub:archetype', mount);
  });
})();
